import {
  buildEndNodeId,
  type GeneratedNodePosition,
  type ProcessDraftStep,
  type SetDraftStepTerminalRequest
} from '@bliks-io/contracts';
import { groupRulesByOutput, positionAfterStep } from '@bliks-io/ui';
import { reconcileEnvelope, removeDraftNodes } from '@/api/draftGraph';
import { applyDraftCache } from '../../../../../api/mutations/helpers/draftCacheHelpers';
import { replaceGeneratedPosition } from '../../../../../api/mutations/helpers/placeholderStep';
import {
  deriveStepPreviousStep,
  removeSourceFromNodeInputs,
  stripStableIdsFromInputs
} from '../../../../../api/mutations/helpers/draftGraphOperations';
import { setDraftStepTerminalApi } from '../../../../../api/persist/draft';
import { expandRuleIdToOutputGroup } from '../graph/expandRuleIdToOutputGroup';
import {
  currentSuccessorStatus,
  findStepByStableId,
  nodeHasAnyRuleSource,
  nodeHasSource,
  rulesPatchWithStatuses,
  rulesPatchWithSuccessorStatus,
  startEventIdsOf,
  taskPatchWithSuccessorStatus
} from './shared';
import type { EdgeActionDeps } from './types';

export type TerminalStatus = SetDraftStepTerminalRequest['status'];

/**
 * Clears a step (or rule branch) from a terminal state in the draft cache
 * before a real successor edge is added — preserves the "terminal XOR
 * connected" invariant: a terminal step can't carry a real successor edge
 * without the editor rendering both the end/unclear marker AND the successor
 * simultaneously. Sends no request: the request that adds the edge clears the
 * marker on the server (`clearEndedSuccessorStatus`).
 *
 * No-op when the step is already non-terminal (status is `''`).
 */
export function ensureNotTerminal(
  stepId: string,
  ruleId: string | undefined,
  deps: EdgeActionDeps
): void {
  const step = findStepByStableId(deps.getCachedSteps(), stepId);
  if (!step) return;

  const prevStatus = currentSuccessorStatus(step, ruleId);
  if (prevStatus !== 'NONE' && prevStatus !== 'UNCLEAR') return;

  const forward = ruleId
    ? rulesPatchWithSuccessorStatus(step, ruleId, '')
    : taskPatchWithSuccessorStatus(step, '');

  // Skip the mutation hook's invalidate-on-settle — a follow-up `runTransaction`
  // would race the refetch with its optimistic writes. Apply the status change
  // directly and let the caller's transaction own the cache.
  applyDraftCache(deps.queryClient, deps.processId, (old) => ({
    ...old,
    steps: old.steps.map((s) =>
      String(s.stableStepId) === stepId || s._id === stepId
        ? ({ ...s, ...(forward as object) } as typeof s)
        : s
    )
  }));
}

/**
 * The handle being marked: `null` is the task handle, otherwise every rule id
 * in the requested output groups.
 */
type HandleRuleIds = ReadonlySet<string> | null;

/**
 * The new state of the marked handle; an `endPositions` entry places that end
 * marker instead of the default.
 */
export type TerminalChange = Pick<SetDraftStepTerminalRequest, 'status' | 'endPositions'>;

/**
 * Flips a step's task handle (`ruleIds` absent) or the given decision outputs
 * into a terminal state and removes what they lead to: downstream steps lose
 * the edge, downstream draft assignments are deleted. Each rule id stands for
 * its whole output group (the rules sharing its `output`, one handle on the
 * canvas). One request, so one undo entry (ADR 0007). No-op when the handle is
 * already at `change.status`.
 */
export async function markTerminal(
  stepId: string,
  ruleIds: readonly string[] | undefined,
  change: TerminalChange,
  deps: EdgeActionDeps
): Promise<void> {
  const step = findStepByStableId(deps.getCachedSteps(), stepId);
  if (!step) return;
  let handle: HandleRuleIds = null;
  let requested: string[] | undefined;
  if (ruleIds) {
    if (step.actionType !== 'decision') return;
    // The server rejects a rule id the step does not carry.
    const groups = ruleIds.map((rid) => expandRuleIdToOutputGroup(step.rules, rid));
    requested = ruleIds.filter((_, i) => groups[i].length > 0);
    handle = new Set(groups.flat());
    if ([...handle].every((rid) => currentSuccessorStatus(step, rid) === change.status)) return;
  } else {
    // A decision routes per output: the server rejects its task handle.
    if (step.actionType === 'decision') return;
    if (currentSuccessorStatus(step, undefined) === change.status) return;
  }
  const given = new Map((change.endPositions ?? []).map((p) => [p.nodeId, p]));
  const endPositions = endNodeIdsOf(step, handle).flatMap((nodeId) => {
    const position =
      given.get(nodeId) ?? defaultEndPosition(String(step.stableStepId), nodeId, deps);
    return position ? [position] : [];
  });
  await setTerminal(
    stepId,
    handle,
    {
      status: change.status,
      ...(requested ? { ruleIds: requested } : {}),
      ...(endPositions.length > 0 ? { endPositions } : {})
    },
    deps
  );
}

/** The canvas's end node per marked output: one per output group's representative. */
function endNodeIdsOf(step: ProcessDraftStep, handle: HandleRuleIds): string[] {
  const stepId = String(step.stableStepId);
  if (handle === null) return [buildEndNodeId(stepId)];
  const { ruleIdToRepresentative } = groupRulesByOutput(step.rules ?? []);
  const branchKeys = new Set<string>();
  for (const rid of handle) {
    const branchKey = ruleIdToRepresentative.get(rid);
    if (branchKey) branchKeys.add(branchKey);
  }
  return [...branchKeys].map((branchKey) => buildEndNodeId(stepId, branchKey));
}

/**
 * The position the canvas would give an end marker (its saved one, else one
 * column right of the step), so the marker's layout entry lands in the
 * terminal request instead of a separate seed write. Undefined when the step
 * has no saved position.
 */
function defaultEndPosition(
  stepId: string,
  nodeId: string,
  deps: EdgeActionDeps
): GeneratedNodePosition | undefined {
  const saved = deps.getCachedGeneratedPositions().find((g) => g.stepId === nodeId);
  if (saved) return { nodeId, x: saved.x, y: saved.y };
  const stepPos = deps.getPositionsById().get(stepId);
  return stepPos && { nodeId, ...positionAfterStep(stepPos) };
}

async function setTerminal(
  stepId: string,
  handle: HandleRuleIds,
  body: SetDraftStepTerminalRequest,
  deps: EdgeActionDeps
): Promise<void> {
  const { processId } = deps;
  const { endPositions } = body;
  const holdsHandleEdge = (node: Parameters<typeof nodeHasSource>[0]) =>
    handle === null ? nodeHasSource(node, stepId) : nodeHasAnyRuleSource(node, stepId, handle);

  await deps.runTransaction({
    optimistic: (prev) => {
      const doomed = new Set(
        prev.draftAssignments.filter(holdsHandleEdge).map((a) => String(a.stableStepId))
      );
      const withoutAssignments = removeDraftNodes(prev, doomed);
      const unchanged = new Set(prev.steps);
      const startEventIds = startEventIdsOf(deps);
      return {
        ...withoutAssignments,
        draft: { ...withoutAssignments.draft, updatedAt: new Date() },
        steps: withoutAssignments.steps.map((s) => {
          if (String(s.stableStepId) === stepId) return endedStep(s, handle, body.status);
          const trimmed =
            handle === null
              ? stripStableIdsFromInputs(s, new Set([stepId]))
              : removeSourceFromNodeInputs(s, stepId, [...handle]);
          if (unchanged.has(trimmed)) return trimmed;
          // ADR 0006: `previousStep` follows the edge set, or the builder
          // redraws the removed edge until the response lands.
          return {
            ...trimmed,
            previousStep: deriveStepPreviousStep(trimmed.inputs, startEventIds)
          } as ProcessDraftStep;
        })
      };
    },
    ...(endPositions
      ? {
          optimisticLayout: (prev) =>
            endPositions.reduce(
              (layout, position) => replaceGeneratedPosition(layout, position),
              prev
            )
        }
      : {}),
    persist: () => setDraftStepTerminalApi(processId, stepId, body),
    reconcile: reconcileEnvelope
  });
}

/** The marked handle ends: its status is set and its successor arrays cleared. */
function endedStep(
  step: ProcessDraftStep,
  handle: HandleRuleIds,
  status: TerminalStatus
): ProcessDraftStep {
  const patch =
    handle === null
      ? taskPatchWithSuccessorStatus(step, status, { clearSuccessorArrays: true })
      : rulesPatchWithStatuses(step, new Map([...handle].map((rid) => [rid, status])), {
          clearSuccessorArrays: true
        });
  return { ...step, ...(patch as object) } as ProcessDraftStep;
}
